/**
 * Error locale keys for translating server error messages on the client.
 * These keys map to translation strings in src/common/locales/en/errors.json and es/errors.json
 *
 * Naming convention: [category].[specificError]
 * Note: These keys are used with the 'errors' translation group, so the full key is 'errors.[key]'
 */

import type { TranslationKey } from '../locales/types/translationTypes';

export const ERROR_LOCALE_KEYS = {
    AUTH: {
        UNAUTHORIZED: 'auth.unauthorized',
        FORBIDDEN: 'auth.forbidden',
        NOT_ADMIN: 'auth.notAdmin',
        TOKEN_EXPIRED: 'auth.tokenExpired',
        EMBARGOED_IP: 'auth.embargoedIp',
        INVALID_CREDENTIALS: 'auth.invalidCredentials',
        ACCOUNT_LOCKED: 'auth.accountLocked',
        PASSWORD_INCORRECT: 'auth.passwordIncorrect',
    },
    VALIDATION: {
        REQUIRED: 'validation.required',
        INVALID: 'validation.invalid',
        EMAIL: {
            INVALID: 'validation.email.invalid',
            ALREADY_EXISTS: 'validation.email.alreadyExists',
            FAKE: 'validation.email.fake',
        },
        EMAIL_INVALID: 'validation.email.invalid',
        PASSWORD: {
            TOO_SHORT: 'validation.password.tooShort',
            TOO_WEAK: 'validation.password.tooWeak',
        },
        MAX_LENGTH: 'validation.maxLength',
        DATE: 'validation.date',
        URL: 'validation.url',
        NUMBER: 'validation.number',
        SELECT: 'validation.select',
        OBJECT: 'validation.object',
        OBJECT_KEY: 'validation.objectKey',
        ELEMENT_ID: 'validation.elementId',
        OBJECT_ID: 'validation.objectId',
        HTML: 'validation.html',
    },
    BOARD: {
        NOT_FOUND: 'board.notFound',
        DELETED: 'board.deleted',
        ACCESS_DENIED: 'board.accessDenied',
        PASSWORD_PROTECTED: 'board.passwordProtected',
    },
    ELEMENT: {
        DUPLICATION_LIMIT_EXCEEDED: 'element.duplication.limitExceeded',
    },
    FILE: {
        MAX_SIZE: 'file.maxSize',
        INVALID_TYPE: 'file.invalidType',
        NO_FILE: 'file.noFile',
        INVALID_FILENAME: 'file.invalidFilename',
        NO_FILENAME: 'file.noFilename',
        INVALID_EXTENSION: 'file.invalidExtension',
        DOT_FILES: 'file.dotFiles',
        UPLOAD_FAILED: 'file.uploadFailed',
        GUEST_NOT_PERMITTED: 'file.guestNotPermitted',
    },
    NETWORK: {
        TIMEOUT: 'network.timeout',
        CONNECTION_LOST: 'network.connectionLost',
        OFFLINE: 'network.offline',
    },
    SERVER: {
        GENERIC: 'server.generic',
        MAINTENANCE: 'server.maintenance',
    },
    SUBSCRIPTION: {
        NOT_SUBSCRIBED: 'subscription.notSubscribed',
    },
    EXPORT: {
        FAILED: 'export.failed',
        TOO_MANY_REQUESTS: 'export.tooManyRequests',
        LIMIT_EXCEEDED: 'export.limitExceeded',
    },
    LIMIT: {
        EXCEEDED: 'limit.exceeded',
        FREE_FILE_COUNT: 'limit.freeFileCount',
    },
    RATE_LIMIT: {
        TOO_MANY_REQUESTS: 'rateLimit.tooManyRequests',
        IP_BANNED: 'rateLimit.ipBanned',
    },
    LINK: {
        FETCH_FAILED: 'link.fetchFailed',
    },
    USER: {
        NOT_FOUND: 'user.notFound',
    },
} as const;

// Use TranslationKey type from resources.ts for proper type checking
export type ErrorLocaleKey = TranslationKey<'errors'>;
